import { lazy, Suspense, useEffect, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { Navigate, Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import type { Session } from '@supabase/supabase-js';
import { supabase } from './lib/supabase';
import { AppLayout } from './layouts/AppLayout';
import { Home } from './screens/Home';
import { JobDetail } from './screens/JobDetail';
import { Login } from './screens/Login';

const LAST_ROUTE_KEY = 'eva-field:last-route';
const LOCAL_DEV_AUTH_BYPASS = import.meta.env.VITE_LOCAL_DEV_AUTH_BYPASS === 'true';
const CameraRoute = lazy(() =>
  import('./screens/CameraRoute').then((m) => ({ default: m.CameraRoute })),
);
const Completion = lazy(() =>
  import('./screens/Completion').then((m) => ({ default: m.Completion })),
);
const Documents = lazy(() =>
  import('./screens/Documents').then((m) => ({ default: m.Documents })),
);
const Notepad = lazy(() =>
  import('./screens/Notepad').then((m) => ({ default: m.Notepad })),
);
const PDFViewer = lazy(() =>
  import('./screens/PDFViewer').then((m) => ({ default: m.PDFViewer })),
);
const Photos = lazy(() =>
  import('./screens/Photos').then((m) => ({ default: m.Photos })),
);
const PostToEVA = lazy(() =>
  import('./screens/PostToEVA').then((m) => ({ default: m.PostToEVA })),
);
const PreSyncReview = lazy(() =>
  import('./screens/PreSyncReview').then((m) => ({ default: m.PreSyncReview })),
);
const Settings = lazy(() =>
  import('./screens/Settings').then((m) => ({ default: m.Settings })),
);
const Summary = lazy(() =>
  import('./screens/Summary').then((m) => ({ default: m.Summary })),
);
const SyncProgress = lazy(() =>
  import('./screens/SyncProgress').then((m) => ({ default: m.SyncProgress })),
);

function RouteLoading({ label = 'Loading…' }: { label?: string }) {
  return <div className="p-4 text-sm text-gray-500">{label}</div>;
}

function LazyRoute({
  children,
  label,
}: {
  children: ReactNode;
  label?: string;
}) {
  return <Suspense fallback={<RouteLoading label={label} />}>{children}</Suspense>;
}

export default function App() {
  const location = useLocation();
  const navigate = useNavigate();
  const restoredRef = useRef(false);
  const locationRef = useRef(location);
  const [session, setSession] = useState<Session | null>(null);
  const [checkingSession, setCheckingSession] = useState(true);
  const [authMessage, setAuthMessage] = useState<string | null>(null);

  useEffect(() => {
    locationRef.current = location;
  }, [location]);

  useEffect(() => {
    let mounted = true;

    const boot = async () => {
      if (LOCAL_DEV_AUTH_BYPASS) {
        if (!mounted) return;
        setSession({} as Session);
        setCheckingSession(false);
        return;
      }
      const { data } = await supabase.auth.getSession();
      if (!mounted) return;
      setSession(data.session ?? null);
      setCheckingSession(false);
    };

    void boot();

    const { data: authSubscription } = supabase.auth.onAuthStateChange((_event, nextSession) => {
      if (LOCAL_DEV_AUTH_BYPASS) return;
      setSession(nextSession);
      if (nextSession) {
        setAuthMessage(null);
      }
    });

    const onAuthRequired = () => {
      if (LOCAL_DEV_AUTH_BYPASS) return;
      const currentLocation = locationRef.current;
      const message = 'Your session expired. Please sign in again.';
      setSession(null);
      setAuthMessage(message);
      if (currentLocation.pathname !== '/login') {
        navigate('/login', {
          replace: true,
          state: {
            from: currentLocation.pathname + currentLocation.search,
            message,
          },
        });
      }
    };

    window.addEventListener('eva-field:auth-required', onAuthRequired);

    return () => {
      mounted = false;
      authSubscription.subscription.unsubscribe();
      window.removeEventListener('eva-field:auth-required', onAuthRequired);
    };
    // navigate/location are intentionally omitted to avoid re-subscribing listeners on route changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Restore last route after login on a cold open.
  useEffect(() => {
    if (checkingSession || !session) return;
    if (restoredRef.current) return;
    restoredRef.current = true;
    if (location.pathname !== '/' || location.search) return;
    let saved: string | null = null;
    try {
      saved = sessionStorage.getItem(LAST_ROUTE_KEY);
    } catch {
      saved = null;
    }
    if (saved && saved !== '/' && saved !== '/login' && saved !== '') {
      navigate(saved, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [checkingSession, session]);

  useEffect(() => {
    if (location.pathname === '/login') return;
    try {
      sessionStorage.setItem(LAST_ROUTE_KEY, location.pathname + location.search);
    } catch {
      // sessionStorage unavailable; silent
    }
  }, [location]);

  if (checkingSession) {
    return <div className="p-4 text-sm text-gray-500">Checking session…</div>;
  }

  if (!session) {
    return (
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route
          path="*"
          element={
            <Navigate
              to="/login"
              replace
              state={{
                from: location.pathname + location.search,
                message: authMessage ?? undefined,
              }}
            />
          }
        />
      </Routes>
    );
  }

  return (
    <Routes>
      <Route path="/login" element={<Navigate to="/" replace />} />
      <Route element={<AppLayout />}>
        <Route path="/" element={<Home />} />
        <Route
          path="/settings"
          element={
            <LazyRoute label="Loading settings…">
              <Settings />
            </LazyRoute>
          }
        />
        <Route path="/job/:jobId" element={<JobDetail />} />
        <Route
          path="/job/:jobId/documents"
          element={
            <LazyRoute label="Loading documents…">
              <Documents />
            </LazyRoute>
          }
        />
        <Route
          path="/job/:jobId/photos"
          element={
            <LazyRoute label="Loading photos…">
              <Photos />
            </LazyRoute>
          }
        />
        <Route
          path="/job/:jobId/summary"
          element={
            <LazyRoute label="Loading summary…">
              <Summary />
            </LazyRoute>
          }
        />
        <Route
          path="/job/:jobId/post-to-eva"
          element={
            <LazyRoute label="Loading post review…">
              <PostToEVA />
            </LazyRoute>
          }
        />
        <Route
          path="/job/:jobId/wrap-up"
          element={
            <LazyRoute label="Loading wrap-up…">
              <PreSyncReview />
            </LazyRoute>
          }
        />
      </Route>
      {/* Camera + PDF viewer + full-page notepad are full-bleed and skip AppLayout */}
      <Route
        path="/job/:jobId/camera"
        element={
          <LazyRoute label="Loading camera…">
            <CameraRoute />
          </LazyRoute>
        }
      />
      <Route
        path="/job/:jobId/documents/:docId"
        element={
          <LazyRoute label="Loading document viewer…">
            <PDFViewer />
          </LazyRoute>
        }
      />
      <Route
        path="/job/:jobId/notepad"
        element={
          <LazyRoute label="Loading notepad…">
            <Notepad />
          </LazyRoute>
        }
      />
      <Route
        path="/job/:jobId/sync"
        element={
          <LazyRoute label="Loading sync…">
            <SyncProgress />
          </LazyRoute>
        }
      />
      <Route
        path="/job/:jobId/complete"
        element={
          <LazyRoute label="Loading completion…">
            <Completion />
          </LazyRoute>
        }
      />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}
